import React from 'react';
import { Link, LinkProps } from 'react-router-dom';

import { Box, Card, CardProps, Typography } from '@mui/material';
import styled from '@emotion/styled';
import { ClipboardCheck } from 'lucide-react';
import { QuoteListItem } from '../../../apis/quoteApi';
import { getQuoteExpiration, getQuoteStatus, quoteStatusColors } from '../utils/quoteUtils';
import CardLabel from '../../../components/common/CardLabel';

const StyledLink = styled(Link)<LinkProps>`
  color: ${(props) => props.theme.palette.primary.light};
  text-decoration: none;

  &:focus,
  &:hover,
  &:visited,
  &:link,
  &:active {
    text-decoration: none;
  }
`;

const TypographyEllipses = styled(Typography)`
  width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
`;

type QuoteCardProps = CardProps & { row: QuoteListItem; backOfficeRootUrl: string | null };

const QuoteCard: React.FC<QuoteCardProps> = ({ row, backOfficeRootUrl }: QuoteCardProps) => {
  const handleCardClicked = (id: string) => {
    window.open(`${backOfficeRootUrl}/Portal/Customers/Quotes/${id}`, '_blank');
  };
  const quoteStatus = getQuoteStatus(row);
  const quoteExpiration = getQuoteExpiration(row);

  return (
    <Card
      onClick={() => handleCardClicked(row.id)}
      title="Press to view"
      sx={{
        mx: 2,
        mt: 2,
        mb: 1,
        display: 'flex',
        border: '1px solid',
        cursor: 'pointer',
        borderColor: (theme) => theme.palette.divider,
        '&:hover': {
          borderColor: (theme) => theme.palette.action.active,
        },
      }}
    >
      <Box
        sx={{
          width: 35,
          minWidth: 35,
          minHeight: 60,
          maxHeight: 190,
          overflow: 'hidden',
        }}
      >
        <Box
          height="100%"
          width="100%"
          display="flex"
          alignItems="center"
          justifyContent="center"
          title={quoteStatus}
          color={(theme) => theme.palette.common.white}
          sx={{
            backgroundColor: () => quoteStatusColors[quoteStatus.toLowerCase() as keyof typeof quoteStatusColors],
          }}
        >
          <ClipboardCheck width="60%" height="60%" />
        </Box>
      </Box>
      <Box pt={2} pl={3} pr={1} pb={1} flexShrink={1} flexGrow={1}>
        <TypographyEllipses variant="h6" mb="6px">
          Quote {row.quoteNumber}: {quoteStatus}
        </TypographyEllipses>
        <Typography
          mb="4px"
          mr={2}
          variant="body2"
          sx={{
            display: 'inline-block',
            fontSize: 14,
            color: (theme) => theme.palette.text.secondary,
          }}
        >
          <CardLabel>Total:</CardLabel> ${row.total.toFixed(2)}
        </Typography>
        <Typography
          mb="3px"
          variant="body2"
          sx={{
            display: 'inline-block',
            fontSize: 14,
            color: (theme) => theme.palette.text.secondary,
          }}
        >
          <CardLabel>Down:</CardLabel> ${row.depositTotal.toFixed(2)}
        </Typography>
        {quoteExpiration && (
          <Typography
            mb="3px"
            variant="body2"
            sx={{
              fontSize: 14,
              color: (theme) => theme.palette.text.secondary,
            }}
          >
            <CardLabel>Expiration:</CardLabel> {quoteExpiration}
          </Typography>
        )}
      </Box>
      <Box width={20} pl={2} mr={3} fontSize={40} display="flex" alignItems="center">
        <StyledLink to="" onClick={() => handleCardClicked(row.id)}>
          &rsaquo;
        </StyledLink>
      </Box>
    </Card>
  );
};

export default QuoteCard;
